import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { FinancialAccountSelectorComponent } from '../../shared/design-system/financial-account-selector.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { FinanceApiService } from '../finance/finance-api.service';
import { PAYMENTS_LIST_PAGE_STYLES, buildPaymentsPayloadHash, paymentStatusLabelKey } from './payments-list-page.utils';
import {
  BnplPlanRow,
  BnplRepaymentRow,
  CreditApplicationRow,
  CreditDecisionRow,
  CreditLineRow,
  DelinquencyCaseRow,
  LoanContractRow,
  LoanInstallmentRow,
  PaymentsAdminService,
  PortfolioAllocationRow,
  PortfolioRepaymentDistributionRow,
  LendingCategoryInterestPolicyRow,
  LendingSellerGovernanceRuleRow
} from './payments-admin.service';

type BnplTab =
  | 'plans'
  | 'categoryPolicies'
  | 'sellerRules'
  | 'applications'
  | 'creditLines'
  | 'contracts'
  | 'installments'
  | 'repayments'
  | 'delinquency';

@Component({
  standalone: true,
  selector: 'ain-payments-bnpl',
  imports: [
    AinPageComponent,
    AinStateComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    SelectModule,
    TagModule,
    AinCanAccessDirective,
    CurrencySelectorComponent,
    CustomerSelectorComponent,
    FinancialAccountSelectorComponent,
    InfoHintComponent,
    AinAmountPipe,
    AinDatePipe
  ],
  template: `
    <ain-page [eyebrow]="t('payments.eyebrow')" [title]="t('payments.bnpl.title')" [description]="t('payments.bnpl.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'payments.bnpl.help.page.title'"
        [descriptionKey]="'payments.bnpl.help.page.description'"
        [bulletKeys]="[
          'payments.bnpl.help.page.bullets.filters',
          'payments.bnpl.help.page.bullets.actions',
          'payments.bnpl.help.page.bullets.portfolio'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('payments.bnpl.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('payments.bnpl.unavailable')" [message]="error() ?? ''" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            @if (activeTab() === 'plans') {
              <button
                pButton
                type="button"
                [label]="t('common.create')"
                ainCanAccess
                [ainCanAccessPermission]="'payments.lending'"
                (click)="openCreatePlanDialog()">
              </button>
            }
            @if (activeTab() === 'applications') {
              <button
                pButton
                type="button"
                [label]="t('payments.bnpl.submitApplication')"
                ainCanAccess
                [ainCanAccessPermission]="'payments.lending'"
                (click)="openSubmitApplicationDialog()">
              </button>
            }
            @if (activeTab() === 'contracts') {
              <button
                pButton
                type="button"
                [label]="t('payments.bnpl.createContract')"
                ainCanAccess
                [ainCanAccessPermission]="'payments.lending'"
                (click)="openCommitDialog()">
              </button>
            }
            <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="reload()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('payments.bnpl.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'payments.bnpl.help.filters.title'"
                [descriptionKey]="'payments.bnpl.help.filters.description'" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('payments.bnpl.customer') }}</span>
                <ain-customer-selector [(ngModel)]="filterCustomerId" name="filterCustomerId" />
              </label>
              <label>
                <span>{{ t('payments.bnpl.filters.status') }}</span>
                <p-select
                  [options]="statusFilterOptions()"
                  optionValue="value"
                  [(ngModel)]="filterStatus"
                  name="filterStatus"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('columns.currency') }}</span>
                <ain-currency-selector [(ngModel)]="filterCurrency" name="filterCurrency" />
              </label>
            </div>
          </details>

          @if (actionError()) {
            <ain-state type="error" [title]="t('payments.bnpl.actionError')" [message]="actionError() ?? ''" />
          }

        <nav class="tabs">
          @for (tab of tabItems; track tab.id) {
            <button type="button" [class.active]="activeTab() === tab.id" (click)="activeTab.set(tab.id)">{{ t(tab.labelKey) }}</button>
          }
        </nav>

        @switch (activeTab()) {
          @case ('plans') {
              <div class="section-title">
                <span>{{ t('payments.bnpl.tabs.plans') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.bnpl.help.tabs.plans.title'"
                  [descriptionKey]="'payments.bnpl.help.tabs.plans.description'" />
              </div>
              <p-table [value]="filteredPlans()">
                <ng-template pTemplate="header">
                  <tr><th>{{ t('payments.bnpl.planName') }}</th><th>{{ t('columns.currency') }}</th><th>{{ t('payments.bnpl.installments') }}</th><th>{{ t('payments.bnpl.interestRate') }}</th><th>{{ t('payments.bnpl.fee') }}</th><th>{{ t('columns.status') }}</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.name }}</td>
                    <td>{{ row.currencyCode }}</td>
                    <td>{{ row.installmentCount }}</td>
                    <td>{{ row.interestRatePercent }}%</td>
                    <td>{{ row.feeAmount | ainAmount }} {{ row.currencyCode }}</td>
                    <td><p-tag [value]="row.isActive ? t('payments.common.enabled') : t('payments.common.disabled')" [severity]="row.isActive ? 'success' : 'warn'"></p-tag></td>
                  </tr>
                </ng-template>
              </p-table>
          }

          @case ('applications') {
              <div class="section-title">
                <span>{{ t('payments.bnpl.tabs.applications') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.bnpl.help.tabs.applications.title'"
                  [descriptionKey]="'payments.bnpl.help.tabs.applications.description'" />
              </div>
              <p-table [value]="filteredApplications()">
                <ng-template pTemplate="header">
                  <tr><th>{{ t('payments.bnpl.customer') }}</th><th>{{ t('payments.bnpl.amount') }}</th><th>{{ t('columns.status') }}</th><th>{{ t('payments.bnpl.actions') }}</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td><code>{{ row.customerAccountId }}</code></td>
                    <td>{{ row.requestedAmount | ainAmount }} {{ row.currencyCode }}</td>
                    <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                    <td class="actions-cell">
                      @if (row.status === 'UnderReview') {
                        <button pButton type="button" size="small" [label]="t('payments.bnpl.approve')" (click)="decide(row, 'Approved')"></button>
                        <button pButton type="button" size="small" severity="danger" [label]="t('payments.bnpl.decline')" (click)="decide(row, 'Declined')"></button>
                      }
                      @if (row.status === 'Approved' && !hasContractForApplication(row.id)) {
                        <button pButton type="button" size="small" [label]="t('payments.bnpl.createContract')" (click)="openCommitDialog(row)"></button>
                      }
                    </td>
                  </tr>
                </ng-template>
              </p-table>
              @if (selectedDecision()) {
                <section class="detail-card">
                  <h4>{{ t('payments.bnpl.decisionDetail') }}</h4>
                  <p>{{ selectedDecision()!.decision }} — {{ selectedDecision()!.reasonCodesJson }}</p>
                </section>
              }
          }

          @case ('creditLines') {
              <div class="section-title">
                <span>{{ t('payments.bnpl.tabs.creditLines') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.bnpl.help.tabs.creditLines.title'"
                  [descriptionKey]="'payments.bnpl.help.tabs.creditLines.description'" />
              </div>
              <p-table [value]="filteredCreditLines()">
                <ng-template pTemplate="header">
                  <tr><th>{{ t('payments.bnpl.customer') }}</th><th>{{ t('payments.bnpl.limit') }}</th><th>{{ t('payments.bnpl.used') }}</th><th>{{ t('payments.bnpl.available') }}</th><th>{{ t('columns.status') }}</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td><code>{{ row.customerAccountId }}</code></td>
                    <td>{{ row.approvedLimit | ainAmount }} {{ row.currencyCode }}</td>
                    <td>{{ row.usedAmount | ainAmount }}</td>
                    <td>{{ row.availableAmount | ainAmount }}</td>
                    <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                  </tr>
                </ng-template>
              </p-table>
          }

          @case ('contracts') {
              <div class="section-title">
                <span>{{ t('payments.bnpl.tabs.contracts') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.bnpl.help.tabs.contracts.title'"
                  [descriptionKey]="'payments.bnpl.help.tabs.contracts.description'" />
              </div>
              <p class="field-hint">{{ t('payments.bnpl.contractFlowHint') }}</p>
              @if (filteredContracts().length === 0) {
                <ain-state type="empty" [title]="t('payments.bnpl.states.emptyContracts')" [message]="t('payments.bnpl.states.emptyContractsMessage')" />
              } @else {
                <p-table [value]="filteredContracts()">
                  <ng-template pTemplate="header">
                    <tr><th>{{ t('payments.bnpl.customer') }}</th><th>{{ t('payments.bnpl.principal') }}</th><th>{{ t('payments.bnpl.totalPayable') }}</th><th>{{ t('columns.status') }}</th><th>{{ t('payments.portfolio.tabs.allocations') }}</th><th>{{ t('payments.bnpl.actions') }}</th></tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td><code>{{ row.customerAccountId }}</code></td>
                      <td>{{ row.principalAmount | ainAmount }} {{ row.currencyCode }}</td>
                      <td>{{ row.totalPayable | ainAmount }}</td>
                      <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                      <td>
                        @if (portfolioAllocationLabel(row.id); as label) {
                          <p-tag [value]="label" severity="info"></p-tag>
                        } @else {
                          <span>{{ t('payments.portfolio.noAllocation') }}</span>
                        }
                      </td>
                      <td class="actions-cell">
                        @if (row.status === 'PendingActivation') {
                          <button pButton type="button" size="small" [label]="t('payments.bnpl.activateContract')" (click)="openActivateDialog(row)"></button>
                        }
                        <button pButton type="button" size="small" [label]="t('payments.bnpl.viewInstallments')" (click)="loadInstallments(row.id)"></button>
                        <button pButton type="button" size="small" [label]="t('payments.portfolio.viewExposure')" (click)="loadPortfolioForLoan(row.id)"></button>
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
              }
              @if (selectedLoanPortfolio().length) {
                <section class="detail-card">
                  <h4>{{ t('payments.portfolio.allocationDetail') }}</h4>
                  <p-table [value]="selectedLoanPortfolio()">
                    <ng-template pTemplate="header">
                      <tr><th>{{ t('payments.portfolio.poolCode') }}</th><th>{{ t('payments.portfolio.principalAllocated') }}</th><th>{{ t('columns.status') }}</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                      <tr>
                        <td><code>{{ row.fundingPoolId }}</code></td>
                        <td>{{ row.principalAllocated | ainAmount }} {{ row.currencyCode }}</td>
                        <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                      </tr>
                    </ng-template>
                  </p-table>
                </section>
              }
          }

          @case ('installments') {
              <div class="section-title">
                <span>{{ t('payments.bnpl.tabs.installments') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.bnpl.help.tabs.installments.title'"
                  [descriptionKey]="'payments.bnpl.help.tabs.installments.description'" />
              </div>
              <p-table [value]="installments()">
                <ng-template pTemplate="header">
                  <tr><th>#</th><th>{{ t('payments.bnpl.dueDate') }}</th><th>{{ t('payments.bnpl.principal') }}</th><th>{{ t('payments.bnpl.interest') }}</th><th>{{ t('payments.bnpl.fee') }}</th><th>{{ t('payments.bnpl.amountDue') }}</th><th>{{ t('payments.bnpl.paid') }}</th><th>{{ t('columns.status') }}</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.sequence }}</td>
                    <td>{{ row.dueDateUtc | ainDate }}</td>
                    <td>{{ row.principalAmount | ainAmount }}</td>
                    <td>{{ row.interestAmount | ainAmount }}</td>
                    <td>{{ row.feeAmount | ainAmount }}</td>
                    <td>{{ row.amountDue | ainAmount }}</td>
                    <td>{{ row.amountPaid | ainAmount }}</td>
                    <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                  </tr>
                </ng-template>
              </p-table>
          }

          @case ('repayments') {
              <div class="section-title">
                <span>{{ t('payments.bnpl.tabs.repayments') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.bnpl.help.tabs.repayments.title'"
                  [descriptionKey]="'payments.bnpl.help.tabs.repayments.description'" />
              </div>
              <p-table [value]="repayments()">
                <ng-template pTemplate="header">
                  <tr><th>{{ t('payments.bnpl.loan') }}</th><th>{{ t('payments.bnpl.amount') }}</th><th>{{ t('payments.lending.idempotencyKey') }}</th><th>{{ t('columns.status') }}</th><th>{{ t('payments.bnpl.actions') }}</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td><code>{{ row.loanContractId }}</code></td>
                    <td>{{ row.amount | ainAmount }} {{ row.currencyCode }}</td>
                    <td>{{ row.idempotencyKey }}</td>
                    <td>{{ row.postingErrorCode || t('payments.common.posted') }}</td>
                    <td><button pButton type="button" size="small" [label]="t('payments.portfolio.viewDistributions')" (click)="loadRepaymentDistributions(row)"></button></td>
                  </tr>
                </ng-template>
              </p-table>
              @if (repaymentDistributions().length) {
                <section class="detail-card">
                  <h4>{{ t('payments.portfolio.tabs.distributions') }}</h4>
                  <p-table [value]="repaymentDistributions()">
                    <ng-template pTemplate="header">
                      <tr><th>{{ t('payments.portfolio.poolCode') }}</th><th>{{ t('payments.portfolio.amount') }}</th><th>{{ t('payments.portfolio.distributedAt') }}</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                      <tr>
                        <td><code>{{ row.fundingPoolId }}</code></td>
                        <td>{{ row.amount | ainAmount }} {{ row.currencyCode }}</td>
                        <td>{{ row.distributedAtUtc }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </section>
              }
          }

          @case ('categoryPolicies') {
              <div class="section-title">
                <span>{{ t('payments.bnpl.tabs.categoryPolicies') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.bnpl.help.tabs.categoryPolicies.title'"
                  [descriptionKey]="'payments.bnpl.help.tabs.categoryPolicies.description'" />
              </div>
              <p-table [value]="categoryPolicies()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.bnpl.governance.code') }}</th>
                    <th>{{ t('payments.bnpl.governance.category') }}</th>
                    <th>{{ t('payments.bnpl.interestRate') }}</th>
                    <th>{{ t('columns.status') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.name }} <code>{{ row.policyCode }}</code></td>
                    <td><code>{{ row.categoryId }}</code></td>
                    <td>{{ row.interestRatePercent }}%</td>
                    <td><p-tag [value]="row.isActive ? t('payments.common.enabled') : t('payments.common.disabled')" [severity]="row.isActive ? 'success' : 'warn'"></p-tag></td>
                  </tr>
                </ng-template>
              </p-table>
          }

          @case ('sellerRules') {
              <div class="section-title">
                <span>{{ t('payments.bnpl.tabs.sellerRules') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.bnpl.help.tabs.sellerRules.title'"
                  [descriptionKey]="'payments.bnpl.help.tabs.sellerRules.description'" />
              </div>
              <p-table [value]="sellerRules()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.bnpl.governance.code') }}</th>
                    <th>{{ t('payments.bnpl.governance.seller') }}</th>
                    <th>{{ t('payments.bnpl.governance.bnplMode') }}</th>
                    <th>{{ t('payments.bnpl.governance.payoutHoldDays') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.name }} <code>{{ row.ruleCode }}</code></td>
                    <td><code>{{ row.sellerId }}</code></td>
                    <td>{{ row.bnplMode }}</td>
                    <td>{{ row.payoutHoldDaysAfterBnplOrder }}</td>
                  </tr>
                </ng-template>
              </p-table>
          }

          @case ('delinquency') {
              <div class="section-title">
                <span>{{ t('payments.bnpl.tabs.delinquency') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.bnpl.help.tabs.delinquency.title'"
                  [descriptionKey]="'payments.bnpl.help.tabs.delinquency.description'" />
              </div>
              <section class="toolbar">
                <button pButton type="button" [label]="t('payments.bnpl.evaluateDelinquency')" (click)="evaluateDelinquency()"></button>
              </section>
              <p-table [value]="delinquencyCases()">
                <ng-template pTemplate="header">
                  <tr><th>{{ t('payments.bnpl.loan') }}</th><th>{{ t('payments.bnpl.overdueCount') }}</th><th>{{ t('payments.bnpl.overdueAmount') }}</th><th>{{ t('columns.status') }}</th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td><code>{{ row.loanContractId }}</code></td>
                    <td>{{ row.overdueInstallmentCount }}</td>
                    <td>{{ row.overdueAmount | ainAmount }}</td>
                    <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                  </tr>
                </ng-template>
              </p-table>
          }
        }
        </section>
      }

      <p-dialog
        [header]="t('payments.bnpl.createPlanTitle')"
        [modal]="true"
        [(visible)]="createPlanDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label>
            <span class="label-row">
              {{ t('payments.bnpl.planName') }}
              <ain-info-hint
                [titleKey]="'payments.bnpl.help.createPlan.title'"
                [descriptionKey]="'payments.bnpl.help.createPlan.description'" />
            </span>
            <input pInputText [(ngModel)]="planModel.name" name="planName" />
          </label>
          <label>
            <span>{{ t('columns.currency') }}</span>
            <ain-currency-selector [(ngModel)]="planModel.currencyCode" name="planCurrencyCode" />
          </label>
          <label>
            <span>{{ t('payments.bnpl.minAmount') }}</span>
            <input pInputText type="number" [(ngModel)]="planModel.minAmount" name="planMinAmount" />
          </label>
          <label>
            <span>{{ t('payments.bnpl.maxAmount') }}</span>
            <input pInputText type="number" [(ngModel)]="planModel.maxAmount" name="planMaxAmount" />
          </label>
          <label>
            <span>{{ t('payments.bnpl.installments') }}</span>
            <input pInputText type="number" [(ngModel)]="planModel.installmentCount" name="planInstallmentCount" />
          </label>
          <label>
            <span>{{ t('payments.bnpl.interestRate') }}</span>
            <input pInputText type="number" [(ngModel)]="planModel.interestRatePercent" name="planInterestRatePercent" />
          </label>
          <label>
            <span>{{ t('payments.bnpl.fee') }}</span>
            <input pInputText type="number" [(ngModel)]="planModel.feeAmount" name="planFeeAmount" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createPlanDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="creatingPlan()" (click)="createPlan()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('payments.bnpl.submitApplicationTitle')"
        [modal]="true"
        [(visible)]="submitApplicationDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.bnpl.customer') }}
              <ain-info-hint
                [titleKey]="'payments.bnpl.help.submitApplication.title'"
                [descriptionKey]="'payments.bnpl.help.submitApplication.description'" />
            </span>
            <ain-customer-selector [(ngModel)]="applicationModel.customerAccountId" name="submitCustomerId" />
          </label>
          <label>
            <span>{{ t('payments.bnpl.amount') }}</span>
            <input pInputText type="number" [(ngModel)]="applicationModel.requestedAmount" name="submitAmount" />
          </label>
          <label>
            <span>{{ t('columns.currency') }}</span>
            <ain-currency-selector [(ngModel)]="applicationModel.currencyCode" name="submitCurrency" />
          </label>
          <label>
            <span>{{ t('payments.bnpl.requestedInstallments') }}</span>
            <input pInputText type="number" [(ngModel)]="applicationModel.requestedInstallments" name="submitInstallments" />
          </label>
          <label>
            <span>{{ t('payments.bnpl.monthlyIncome') }}</span>
            <input pInputText type="number" [(ngModel)]="applicationModel.monthlyIncome" name="submitIncome" />
          </label>
          <label>
            <span>{{ t('payments.bnpl.existingDebt') }}</span>
            <input pInputText type="number" [(ngModel)]="applicationModel.existingDebt" name="submitDebt" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="submitApplicationDialogVisible = false"></button>
          <button pButton type="button" [label]="t('payments.bnpl.submitApplication')" [disabled]="submittingApplication()" (click)="submitApplication()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('payments.bnpl.createContractTitle')"
        [modal]="true"
        [(visible)]="commitDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.bnpl.selectApplication') }}
              <ain-info-hint
                [titleKey]="'payments.bnpl.help.createContract.title'"
                [descriptionKey]="'payments.bnpl.help.createContract.description'" />
            </span>
            <p-select
              [options]="commitApplicationOptions()"
              [(ngModel)]="commitModel.creditApplicationId"
              name="commitApplicationId"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              appendTo="body"
              (ngModelChange)="onCommitApplicationChanged()"
              [placeholder]="t('payments.bnpl.selectApplication')">
            </p-select>
          </label>
          <label class="wide">
            <span>{{ t('payments.bnpl.selectPlan') }}</span>
            <p-select
              [options]="commitPlanOptions()"
              [(ngModel)]="commitModel.planId"
              name="commitPlanId"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              appendTo="body"
              [placeholder]="t('payments.bnpl.selectPlan')">
            </p-select>
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="commitDialogVisible = false"></button>
          <button pButton type="button" [label]="t('payments.bnpl.createContract')" [disabled]="committingContract()" (click)="commitContract()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('payments.bnpl.activateContractTitle')"
        [modal]="true"
        [(visible)]="activateDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.bnpl.activateContractPosting') }}
              <ain-info-hint
                [titleKey]="'payments.bnpl.help.activateContract.title'"
                [descriptionKey]="'payments.bnpl.help.activateContract.description'" />
            </span>
            <p-select [options]="bookOptions()" [(ngModel)]="postingBookId" name="activateBookId" optionLabel="label" optionValue="value" appendTo="body" (ngModelChange)="onBookChanged()" [placeholder]="t('finance.journal.book')"></p-select>
          </label>
          <label class="wide">
            <span>{{ t('finance.journal.period') }}</span>
            <p-select [options]="periodOptions()" [(ngModel)]="postingPeriodId" name="activatePeriodId" optionLabel="label" optionValue="value" appendTo="body" [placeholder]="t('finance.journal.period')"></p-select>
          </label>
          <label class="wide">
            <span>{{ t('payments.financialCompleteness.debitAccount') }}</span>
            <ain-financial-account-selector [(ngModel)]="postingDebitAccountId" name="activateDebitAccountId" [bookId]="postingBookId"></ain-financial-account-selector>
          </label>
          <label class="wide">
            <span>{{ t('payments.financialCompleteness.creditAccount') }}</span>
            <ain-financial-account-selector [(ngModel)]="postingCreditAccountId" name="activateCreditAccountId" [bookId]="postingBookId"></ain-financial-account-selector>
          </label>
          <label>
            <span>{{ t('columns.currency') }}</span>
            <ain-currency-selector [(ngModel)]="postingCurrencyCode" name="activateCurrencyCode"></ain-currency-selector>
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="activateDialogVisible = false"></button>
          <button pButton type="button" [label]="t('payments.bnpl.activateContract')" [disabled]="activatingContract()" (click)="activateContract()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .detail-card { margin-top: 12px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; }
    .label-row { display: inline-flex; align-items: center; gap: 6px; }
    .field-hint { margin: 0 0 12px; color: #64748b; font-size: 12px; line-height: 1.6; }
    ${PAYMENTS_LIST_PAGE_STYLES}
  `]
})
export class PaymentsBnplComponent implements OnInit {
  private readonly api = inject(PaymentsAdminService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly localizationService = inject(LocalizationService);

  readonly tabItems: { id: BnplTab; labelKey: string }[] = [
    { id: 'plans', labelKey: 'payments.bnpl.tabs.plans' },
    { id: 'categoryPolicies', labelKey: 'payments.bnpl.tabs.categoryPolicies' },
    { id: 'sellerRules', labelKey: 'payments.bnpl.tabs.sellerRules' },
    { id: 'applications', labelKey: 'payments.bnpl.tabs.applications' },
    { id: 'creditLines', labelKey: 'payments.bnpl.tabs.creditLines' },
    { id: 'contracts', labelKey: 'payments.bnpl.tabs.contracts' },
    { id: 'installments', labelKey: 'payments.bnpl.tabs.installments' },
    { id: 'repayments', labelKey: 'payments.bnpl.tabs.repayments' },
    { id: 'delinquency', labelKey: 'payments.bnpl.tabs.delinquency' }
  ];
  readonly activeTab = signal<BnplTab>('plans');

  readonly plans = signal<BnplPlanRow[]>([]);
  readonly categoryPolicies = signal<LendingCategoryInterestPolicyRow[]>([]);
  readonly sellerRules = signal<LendingSellerGovernanceRuleRow[]>([]);
  readonly applications = signal<CreditApplicationRow[]>([]);
  readonly creditLines = signal<CreditLineRow[]>([]);
  readonly contracts = signal<LoanContractRow[]>([]);
  readonly installments = signal<LoanInstallmentRow[]>([]);
  readonly repayments = signal<BnplRepaymentRow[]>([]);
  readonly delinquencyCases = signal<DelinquencyCaseRow[]>([]);
  readonly selectedDecision = signal<CreditDecisionRow | null>(null);
  readonly loanPortfolioAllocations = signal<PortfolioAllocationRow[]>([]);
  readonly selectedLoanPortfolio = signal<PortfolioAllocationRow[]>([]);
  readonly repaymentDistributions = signal<PortfolioRepaymentDistributionRow[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);

  filterCustomerId = '';
  filterStatus: string | null = null;
  filterCurrency = 'IRR';
  createPlanDialogVisible = false;
  submitApplicationDialogVisible = false;
  commitDialogVisible = false;
  activateDialogVisible = false;
  readonly creatingPlan = signal(false);
  readonly submittingApplication = signal(false);
  readonly committingContract = signal(false);
  readonly activatingContract = signal(false);
  readonly activateTargetContractId = signal<string | null>(null);
  bookOptions = signal<{ label: string; value: string }[]>([]);
  periodOptions = signal<{ label: string; value: string }[]>([]);
  postingBookId = '';
  postingPeriodId = '';
  postingDebitAccountId = '';
  postingCreditAccountId = '';
  postingCurrencyCode = 'IRR';
  activateIdempotencyKey = '';

  applicationModel = {
    customerAccountId: '',
    requestedAmount: 0,
    currencyCode: 'IRR',
    requestedInstallments: 6,
    monthlyIncome: 0,
    existingDebt: 0
  };

  commitModel = {
    creditApplicationId: '',
    planId: ''
  };

  planModel = {
    name: '',
    currencyCode: 'IRR',
    minAmount: 100,
    maxAmount: 100000,
    installmentCount: 6,
    interestRatePercent: 0,
    feeAmount: 0,
    downPaymentPercent: 0,
    isActive: true
  };

  readonly commitApplicationOptions = computed(() =>
    this.applications()
      .filter(row => row.status === 'Approved' && !this.hasContractForApplication(row.id))
      .map(row => ({
        label: `${this.formatAmount(row.requestedAmount)} ${row.currencyCode} — ${row.id.slice(0, 8)}`,
        value: row.id
      }))
  );

  ngOnInit(): void {
    this.financeApi.listBooks().subscribe({
      next: books => {
        this.bookOptions.set(books.map(book => ({ label: `${book.code} — ${book.name}`, value: book.id })));
        this.postingBookId = books[0]?.id ?? '';
        if (this.postingBookId) this.onBookChanged();
      }
    });
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.actionError.set(null);

    const customerFilter = this.filterCustomerId.trim() || undefined;
    let pending = 8;
    const done = (): void => {
      pending -= 1;
      if (pending <= 0) this.loading.set(false);
    };

    this.api.listBnplPlans(undefined, this.filterCurrency).subscribe({
      next: rows => { this.plans.set(rows); done(); },
      error: () => { this.error.set(this.t('payments.bnpl.errorLoad')); this.loading.set(false); }
    });
    this.api.listLendingCategoryInterestPolicies().subscribe({
      next: rows => { this.categoryPolicies.set(rows); done(); },
      error: () => { this.error.set(this.t('payments.bnpl.errorLoad')); this.loading.set(false); }
    });
    this.api.listLendingSellerGovernanceRules().subscribe({
      next: rows => { this.sellerRules.set(rows); done(); },
      error: () => { this.error.set(this.t('payments.bnpl.errorLoad')); this.loading.set(false); }
    });
    this.api.listCreditApplications(customerFilter).subscribe({
      next: response => { this.applications.set(response.items); done(); },
      error: () => { this.error.set(this.t('payments.bnpl.errorLoad')); this.loading.set(false); }
    });
    this.api.listCreditLines(customerFilter).subscribe({
      next: rows => { this.creditLines.set(rows); done(); },
      error: () => { this.error.set(this.t('payments.bnpl.errorLoad')); this.loading.set(false); }
    });
    this.api.listLoanContracts(customerFilter).subscribe({
      next: rows => { this.contracts.set(rows); done(); },
      error: () => { this.error.set(this.t('payments.bnpl.errorLoad')); this.loading.set(false); }
    });
    this.api.listBnplRepayments().subscribe({
      next: response => { this.repayments.set(response.items); done(); },
      error: () => { this.error.set(this.t('payments.bnpl.errorLoad')); this.loading.set(false); }
    });
    this.api.listDelinquencyCases().subscribe({
      next: rows => { this.delinquencyCases.set(rows); done(); },
      error: () => { this.error.set(this.t('payments.bnpl.errorLoad')); this.loading.set(false); }
    });
    this.api.listPortfolioAllocations({ page: 1, pageSize: 100 }).subscribe({
      next: response => this.loanPortfolioAllocations.set(response.items),
      error: () => { /* portfolio tab optional */ }
    });
  }

  filteredPlans(): BnplPlanRow[] {
    return this.plans().filter(row => !this.filterStatus || String(row.isActive) === this.filterStatus);
  }

  filteredApplications(): CreditApplicationRow[] {
    return this.applications().filter(row => !this.filterStatus || row.status === this.filterStatus);
  }

  filteredCreditLines(): CreditLineRow[] {
    return this.creditLines().filter(row => !this.filterCurrency || row.currencyCode === this.filterCurrency);
  }

  filteredContracts(): LoanContractRow[] {
    return this.contracts().filter(row => !this.filterStatus || row.status === this.filterStatus);
  }

  hasContractForApplication(applicationId: string): boolean {
    return this.contracts().some(row => row.creditApplicationId === applicationId);
  }

  commitPlanOptions(): { label: string; value: string }[] {
    const application = this.applications().find(row => row.id === this.commitModel.creditApplicationId);
    if (!application) return [];
    return this.plans()
      .filter(plan =>
        plan.isActive &&
        plan.currencyCode === application.currencyCode &&
        application.requestedAmount >= plan.minAmount &&
        application.requestedAmount <= plan.maxAmount)
      .map(plan => ({
        label: `${plan.name} (${plan.installmentCount} × ${plan.interestRatePercent}% + ${this.formatAmount(plan.feeAmount)})`,
        value: plan.id
      }));
  }

  openSubmitApplicationDialog(): void {
    this.applicationModel = {
      customerAccountId: this.filterCustomerId || '',
      requestedAmount: 0,
      currencyCode: this.filterCurrency || 'IRR',
      requestedInstallments: 6,
      monthlyIncome: 0,
      existingDebt: 0
    };
    this.actionError.set(null);
    this.submitApplicationDialogVisible = true;
  }

  submitApplication(): void {
    const customerAccountId = this.applicationModel.customerAccountId.trim();
    const requestedAmount = Number(this.applicationModel.requestedAmount);
    if (!customerAccountId || requestedAmount <= 0) return;

    const idempotencyKey = `admin-bnpl-app-${Date.now()}`;
    this.submittingApplication.set(true);
    this.api.submitCreditApplication({
      customerAccountId,
      sourceModule: 'admin',
      sourceEntityName: 'payments-bnpl',
      sourceEntityId: idempotencyKey,
      requestedAmount,
      currencyCode: this.applicationModel.currencyCode,
      purpose: 'admin-manual',
      requestedInstallments: Number(this.applicationModel.requestedInstallments) || 6,
      monthlyIncome: Number(this.applicationModel.monthlyIncome),
      existingDebt: Number(this.applicationModel.existingDebt),
      idempotencyKey,
      idempotencyPayloadHash: idempotencyKey,
      correlationId: 'admin-bnpl-ui'
    }).subscribe({
      next: created => {
        this.applications.update(rows => [created, ...rows.filter(item => item.id !== created.id)]);
        this.submitApplicationDialogVisible = false;
        this.submittingApplication.set(false);
        this.activeTab.set('applications');
        if (created.status === 'Approved' && !this.hasContractForApplication(created.id)) {
          this.openCommitDialog(created);
        }
      },
      error: err => {
        this.submittingApplication.set(false);
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.bnpl.errorSubmitApplication'));
      }
    });
  }

  openCommitDialog(application?: CreditApplicationRow): void {
    const firstOption = application?.id ?? this.commitApplicationOptions()[0]?.value ?? '';
    this.commitModel = {
      creditApplicationId: firstOption,
      planId: ''
    };
    this.onCommitApplicationChanged();
    this.actionError.set(null);
    this.commitDialogVisible = true;
  }

  onCommitApplicationChanged(): void {
    const options = this.commitPlanOptions();
    this.commitModel.planId = options[0]?.value ?? '';
  }

  commitContract(): void {
    if (!this.commitModel.creditApplicationId || !this.commitModel.planId) return;
    const idempotencyKey = `admin-bnpl-commit-${Date.now()}`;
    this.committingContract.set(true);
    this.api.commitBnplContract({
      creditApplicationId: this.commitModel.creditApplicationId,
      planId: this.commitModel.planId,
      idempotencyKey,
      idempotencyPayloadHash: idempotencyKey
    }).subscribe({
      next: result => {
        this.contracts.update(rows => [result.loanContract, ...rows.filter(item => item.id !== result.loanContract.id)]);
        this.commitDialogVisible = false;
        this.committingContract.set(false);
        this.activeTab.set('contracts');
      },
      error: err => {
        this.committingContract.set(false);
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.bnpl.errorCreateContract'));
      }
    });
  }

  openActivateDialog(contract: LoanContractRow): void {
    this.activateTargetContractId.set(contract.id);
    this.activateIdempotencyKey = `admin-bnpl-activate-${contract.id}-${Date.now()}`;
    this.postingCurrencyCode = contract.currencyCode;
    this.actionError.set(null);
    this.activateDialogVisible = true;
  }

  onBookChanged(): void {
    if (!this.postingBookId) return;
    this.financeApi.listFiscalPeriods(this.postingBookId).subscribe({
      next: periods => {
        this.periodOptions.set(periods.map(period => ({
          label: `${period.fiscalYear}/${period.periodNumber} (${period.status})`,
          value: period.id
        })));
        this.postingPeriodId = periods.find(item => item.status.toLowerCase() === 'open')?.id ?? periods[0]?.id ?? '';
      }
    });
  }

  activateContract(): void {
    const contractId = this.activateTargetContractId();
    if (!contractId || !this.postingBookId || !this.postingPeriodId || !this.postingDebitAccountId || !this.postingCreditAccountId) {
      this.actionError.set(this.t('payments.financialCompleteness.errorPostingProfile'));
      return;
    }

    const postingProfile = {
      bookId: this.postingBookId,
      periodId: this.postingPeriodId,
      debitAccountId: this.postingDebitAccountId,
      creditAccountId: this.postingCreditAccountId,
      currencyCode: this.postingCurrencyCode
    };
    const idempotencyKey = this.activateIdempotencyKey || `admin-bnpl-activate-${contractId}-${Date.now()}`;
    const idempotencyPayloadHash = buildPaymentsPayloadHash({ contractId, postingProfile });
    this.activatingContract.set(true);
    this.api.activateLoanContract(contractId, {
      idempotencyKey,
      idempotencyPayloadHash,
      postingProfile
    }).subscribe({
      next: updated => {
        this.contracts.update(rows => rows.map(row => row.id === updated.id ? updated : row));
        this.activateDialogVisible = false;
        this.activatingContract.set(false);
      },
      error: err => {
        this.activatingContract.set(false);
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.bnpl.errorActivateContract'));
      }
    });
  }

  openCreatePlanDialog(): void {
    this.planModel = {
      name: '',
      currencyCode: 'IRR',
      minAmount: 100,
      maxAmount: 100000,
      installmentCount: 6,
      interestRatePercent: 0,
      feeAmount: 0,
      downPaymentPercent: 0,
      isActive: true
    };
    this.createPlanDialogVisible = true;
  }

  statusFilterOptions(): { value: string; labelKey: string }[] {
    const statuses = new Set<string>();
    this.applications().forEach(row => statuses.add(row.status));
    this.contracts().forEach(row => statuses.add(row.status));
    this.creditLines().forEach(row => statuses.add(row.status));
    this.delinquencyCases().forEach(row => statuses.add(row.status));
    statuses.add('true');
    statuses.add('false');
    return [...statuses].sort().map(value => ({
      value,
      labelKey: value === 'true' || value === 'false'
        ? (value === 'true' ? 'payments.common.enabled' : 'payments.common.disabled')
        : paymentStatusLabelKey(value)
    }));
  }

  statusLabel(status: string): string {
    const key = paymentStatusLabelKey(status);
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  createPlan(): void {
    if (!this.planModel.name.trim()) return;
    this.creatingPlan.set(true);
    this.api.createBnplPlan({
      name: this.planModel.name.trim(),
      currencyCode: this.planModel.currencyCode,
      minAmount: Number(this.planModel.minAmount),
      maxAmount: Number(this.planModel.maxAmount),
      installmentCount: Number(this.planModel.installmentCount),
      interestRatePercent: Number(this.planModel.interestRatePercent),
      feeAmount: Number(this.planModel.feeAmount),
      downPaymentPercent: Number(this.planModel.downPaymentPercent),
      isActive: this.planModel.isActive,
      policyJson: '{}'
    }).subscribe({
      next: created => {
        this.plans.update(rows => [created, ...rows]);
        this.creatingPlan.set(false);
        this.createPlanDialogVisible = false;
      },
      error: err => {
        this.creatingPlan.set(false);
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.bnpl.errorCreate'));
      }
    });
  }

  decide(application: CreditApplicationRow, decision: string): void {
    const reason = decision === 'Declined' ? '["admin_manual_decline"]' : '["admin_manual_approve"]';
    this.api.decideCreditApplication(application.id, {
      decision,
      approvedAmount: application.requestedAmount,
      maxInstallments: 6,
      interestRatePercent: 0,
      feeAmount: 0,
      reasonCodesJson: reason
    }).subscribe({
      next: result => {
        this.selectedDecision.set(result);
        this.applications.update(rows => rows.map(row => row.id === application.id ? { ...row, status: decision === 'Approved' ? 'Approved' : 'Declined' } : row));
      },
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.bnpl.errorDecide'))
    });
  }

  loadInstallments(loanContractId: string): void {
    this.api.listLoanInstallments(loanContractId).subscribe({
      next: rows => this.installments.set(rows),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.bnpl.errorLoad'))
    });
    this.loadPortfolioForLoan(loanContractId);
  }

  loadPortfolioForLoan(loanContractId: string): void {
    this.api.listPortfolioAllocations({ loanContractId, page: 1, pageSize: 100 }).subscribe({
      next: response => this.selectedLoanPortfolio.set(response.items),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorLoad'))
    });
  }

  loadRepaymentDistributions(repayment: BnplRepaymentRow): void {
    this.api.listRepaymentDistributions({ repaymentId: repayment.id, loanContractId: repayment.loanContractId, page: 1, pageSize: 100 }).subscribe({
      next: response => this.repaymentDistributions.set(response.items),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorLoad'))
    });
  }

  portfolioAllocationLabel(loanContractId: string): string | null {
    const rows = this.loanPortfolioAllocations().filter(row => row.loanContractId === loanContractId && row.status !== 'Released');
    if (!rows.length) return null;
    const active = rows.find(row => row.status === 'Active');
    return active
      ? `${this.formatAmount(active.principalAllocated)} ${active.currencyCode}`
      : this.statusLabel(rows[0].status);
  }

  evaluateDelinquency(): void {
    this.api.evaluateDelinquency().subscribe({
      next: rows => this.delinquencyCases.set(rows),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.bnpl.errorEvaluate'))
    });
  }

  statusSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' {
    if (status === 'Approved' || status === 'Active' || status === 'Paid' || status === 'Resolved') return 'success';
    if (status === 'PendingActivation') return 'warn';
    if (status === 'UnderReview' || status === 'Pending' || status === 'Overdue') return 'warn';
    if (status === 'Declined' || status === 'Cancelled' || status === 'Open') return status === 'Open' ? 'danger' : 'danger';
    return 'info';
  }

  formatAmount(value: number): string {
    return new Intl.NumberFormat(this.localizationService.activeCulture(), {
      maximumFractionDigits: 0
    }).format(value);
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  private parseApiError(err: unknown): string | null {
    const body = (err as { error?: { error?: string; Error?: string } })?.error;
    return body?.error ?? body?.Error ?? null;
  }
}
